<script setup lang="ts">
import { UIIcon } from '@groover-dev/groover-ui'
import { storeToRefs } from 'pinia'

import { useGetMediaUrl } from '~/composables/useGetMediaUrl'

import { useBandSignupStore } from '~/stores/bandSignup'
import { useMiscResizeStore } from '~/stores/miscResize'
import { useUserStore } from '~/stores/user'

import { Breakpoints } from '~/enums/breakpoints'

const { getMediaUrl } = useGetMediaUrl()
const { availableLocales } = useI18n()
const localePath = useLocalePath()
const route = useRoute()

const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore())
const { step: BAND_SIGNUP_STEP } = storeToRefs(useBandSignupStore())
const { IS_LOGGED_IN, IS_INCOMPLETE_USER } = storeToRefs(useUserStore())

const { SET_STEP: BAND_SIGNUP_SET_STEP } = useBandSignupStore()

const isBandSignup = computed<boolean>(
  () =>
    route.path.includes('/band/signup/') && !route.path.includes('discovery'),
)

const isSignup = computed<boolean>(() => route.path.includes('/signup/'))

const isIndex = computed<boolean>(() => {
  const availableIndexRoutes = availableLocales.map((lang) => {
    return `/${lang}/`
  })

  return availableIndexRoutes.includes(route.path) && !IS_LOGGED_IN.value
})

function handleChevronLeftClick() {
  if (route.path.includes('/discovery/'))
    return navigateTo(localePath('/band/signup/'))
  else BAND_SIGNUP_SET_STEP(BAND_SIGNUP_STEP.value - 1)
}
</script>

<template>
  <div
    id="navIndexWrapper"
    class="navIndexWrapper tw-relative tw-z-[10001] tw-w-full tw-border-b tw-border-discrete tw-bg-fill-alt tw-shadow-[shadow:var(--shadow-regular)]"
    :class="{
      'tw-bg-[color:unset] tw-shadow-none':
        isIndex && SCREEN_WIDTH < Breakpoints.md,
    }"
  >
    <div
      class="mainParentContainer tw-z-50 !tw-flex tw-h-15 tw-items-center tw-justify-between tw-gap-4 tw-px-0 tw-py-5 md:tw-h-18 md:tw-py-4"
    >
      <button
        v-if="isBandSignup && SCREEN_WIDTH < Breakpoints.md"
        type="button"
        :aria-label="$t('common.goBack')"
        :class="{ 'tw-opacity-0': BAND_SIGNUP_STEP === 0 }"
        @click="handleChevronLeftClick"
      >
        <UIIcon
          name="fa6-solid:chevron-left"
          class="tw-text-icon-inverse"
          aria-hidden
          size="sm"
        />
      </button>
      <NuxtLinkLocale class="tw-block tw-shrink-0" to="/" no-prefetch>
        <img
          v-if="SCREEN_WIDTH < Breakpoints.md || SCREEN_WIDTH >= Breakpoints.lg"
          :src="getMediaUrl('GrooverLogoWithText-Primary.svg')"
          class="tw-object-contain tw-object-center"
          width="136px"
          alt="Groover logo"
          role="img"
        />
        <LazyLogoIcon v-else size="xl" aria-label="Groover logo" />
      </NuxtLinkLocale>
      <!-- ClientOnly used to keep dynamic content out of cdn cache -->
      <ClientOnly>
        <div
          v-if="SCREEN_WIDTH >= Breakpoints.md && !isSignup"
          class="tw-hidden tw-items-center md:tw-flex"
        >
          <Transition name="fade" mode="out-in">
            <LazyMediaQueryNavLoggedIn
              v-if="IS_LOGGED_IN && !IS_INCOMPLETE_USER"
              key="loggedIn"
              hydrate-on-media-query="(min-width: 768px)"
            />
            <LazyMediaQueryNavLoggedOut
              v-else
              key="loggedOut"
              hydrate-on-media-query="(min-width: 768px)"
            />
          </Transition>
        </div>
        <LazyMediaQueryNavMobile
          v-else-if="!isSignup"
          hydrate-on-media-query="(max-width: 767px)"
        />
      </ClientOnly>
    </div>
  </div>
</template>
